import React from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TIntegrationSelectProps } from '@/console/features/integrations/components/integration-select/integration-select.types';
import { useIntegrationSelectOptions } from '@/console/features/integrations/hooks/use-integration-select-options/use-integration-select-options';
import { TSelectOption } from '@/shared/components/ui/form/components/searchable-select/base-searchable-select.types';
import { AsyncSearchableSelect } from '@/shared/components/ui/form/components/searchable-select/variant/async/async-searchable-select';
import { useReadAllIntegrationsQuery } from '@/shared/redux/api/integration/integrations-api';
import {
  IntegrationFragment,
  ReadAllIntegrationsQuery,
} from '@/shared/types/types.generated';

export const IntegrationSelect = <
  TFieldValues extends FieldValues,
  TOptionShape = string,
>({
  name,
  integrationFilter,
  getItemId,
  createOption,
  disableTags = true,
  enableTagsTooltip,
  filterSelectedTags,
  additionalOptions = [],
  onSelectChange,
  required,
  rtkQueryArgs,
  optionProps,
  disabled,
  multiple = true,
}: TIntegrationSelectProps<TFieldValues, TOptionShape>) => {
  const { t } = useTranslation();
  const { getAdditionalInfo, isDisabled } =
    useIntegrationSelectOptions(optionProps);

  const createDefaultOption = (integration: IntegrationFragment) => {
    return {
      label: integration.name,
      value: integration.integrationId,
      additionalInfo: getAdditionalInfo(integration),
      disabled: isDisabled(integration),
    } as TSelectOption<TOptionShape>;
  };
  const _createOption = createOption ? createOption : createDefaultOption;

  const transformData = (data?: ReadAllIntegrationsQuery) => {
    if (!data) {
      return [];
    }

    const integrations = data.readAllIntegrations.items;
    const validIntegrations = integrationFilter
      ? integrations.filter((integration) => integrationFilter(integration))
      : integrations;

    const options = validIntegrations.map((integration) =>
      _createOption(integration),
    );

    return [...additionalOptions, ...options];
  };

  return (
    <AsyncSearchableSelect<TFieldValues, ReadAllIntegrationsQuery, TOptionShape>
      name={name}
      label={t('integrations:select_integrations', { count: multiple ? 2 : 1 })}
      placeholder={t('integrations:search_integrations', {
        count: multiple ? 2 : 1,
      })}
      disableTags={disableTags}
      enableTagsTooltip={enableTagsTooltip}
      filterSelectedTags={filterSelectedTags}
      multiple={multiple}
      getItemId={getItemId}
      optionProps={{
        useRTKQuery: useReadAllIntegrationsQuery,
        transformData,
        rtkQueryArgs,
      }}
      onSelectChange={onSelectChange}
      required={required}
      disabled={disabled}
    />
  );
};
